// Plant growth model, stated plainly: growth = min(light, water, soil), minus a penalty when
// water is far above light (roots drown) or light far above water (leaves scorch). Values 0..1.

export const SOILS = [
  { id: 'sand', label: 'Sand', quality: 0.5, note: 'Water runs straight through sand.' },
  { id: 'clay', label: 'Clay', quality: 0.7, note: 'Clay holds water but packs tight around roots.' },
  { id: 'loam', label: 'Loam', quality: 1.0, note: 'Loam is the dark crumbly soil gardeners love.' },
] as const;
export type SoilId = typeof SOILS[number]['id'];

export const IMBALANCE_SLACK = 0.3;   // how far water and light may differ before a penalty

export function soilQuality(id: SoilId): number { return SOILS.find(s => s.id === id)!.quality; }

/** light and water as 0..1 */
export function growth(light: number, water: number, soil: SoilId): number {
  const base = Math.min(light, water, soilQuality(soil));
  const drown = Math.max(0, water - light - IMBALANCE_SLACK);
  const scorch = Math.max(0, light - water - IMBALANCE_SLACK);
  return Math.max(0, Math.min(1, base - 0.8 * (drown + scorch)));
}

/** the water level where more water stops helping, for a given light and soil */
export function waterOptimum(light: number, soil: SoilId): number {
  return Math.min(light, soilQuality(soil));
}

/** tree parameters from growth: levels of branching and leaf size */
export function treeFor(g: number, splits: number): { levels: number; leafScale: number } {
  const maxLv = splits === 2 ? 9 : splits === 3 ? 7 : 6;
  const levels = Math.max(2, Math.round(2 + g * (maxLv - 2)));
  return { levels, leafScale: 0.4 + g * 0.8 };
}
